import { buildFlowThemeCardMotion, type FlowThemeCardMotion } from './cardMotion';
import type { FlowThemeCategoryId, FlowThemeDefinition } from './definition';
import type { FlowThemeId } from './engine/types';
import { auroraTheme } from './library/aurora';
import { balloonsTheme } from './library/balloons';
import { bloomTheme } from './library/bloom';
import { cityTheme } from './library/city';
import { clockworkTheme } from './library/clockwork';
import { cosmosTheme } from './library/cosmos';
import { greenhouseTheme } from './library/greenhouse';
import { lanternsTheme } from './library/lanterns';
import { lighthouseTheme } from './library/lighthouse';
import { lifePlanTheme } from './library/life_plan';
import { mosaicTheme } from './library/mosaic';
import { mountainTheme } from './library/mountain';
import { oceanTheme } from './library/ocean';
import { origamiTheme } from './library/origami';
import { pixelTheme } from './library/pixel';
import { pyramidTheme } from './library/pyramid';
import { railwayTheme } from './library/railway';
import { riverTheme } from './library/river';
import { seasonsTheme } from './library/seasons';
import { treehouseTheme } from './library/treehouse';
import { zenTheme } from './library/zen';

export type {
  FlowThemeBursts,
  FlowThemeCardGlass,
  FlowThemeCategoryId,
  FlowThemeDefinition,
  LottieData,
} from './definition';

export const DEFAULT_FLOW_THEME_ID: FlowThemeId = 'mountain';

/**
 * Every theme, in admin picker order within each category. Each theme lives
 * in its own folder under `library/`; registering one is a single line here.
 */
export const FLOW_THEMES: readonly FlowThemeDefinition[] = [
  mountainTheme,
  pyramidTheme,
  oceanTheme,
  bloomTheme,
  lifePlanTheme,
  auroraTheme,
  lanternsTheme,
  cityTheme,
  cosmosTheme,
  lighthouseTheme,
  seasonsTheme,
  balloonsTheme,
  origamiTheme,
  zenTheme,
  greenhouseTheme,
  riverTheme,
  treehouseTheme,
  pixelTheme,
  clockworkTheme,
  railwayTheme,
  mosaicTheme,
];

const THEMES_BY_ID = new Map(FLOW_THEMES.map((theme) => [theme.id, theme]));

export interface FlowThemeCategory {
  id: FlowThemeCategoryId;
  label: string;
  themes: readonly FlowThemeDefinition[];
}

/** Admin picker groups, in order. Each theme names its group in its definition. */
const FLOW_THEME_CATEGORY_LABELS: ReadonlyArray<{ id: FlowThemeCategoryId; label: string }> = [
  { id: 'classic', label: 'Classic' },
  { id: 'night', label: 'Night & Sky' },
  { id: 'daylight', label: 'Daylight & Nature' },
  { id: 'playful', label: 'Playful & Crafted' },
];

export const FLOW_THEME_CATEGORIES: readonly FlowThemeCategory[] = FLOW_THEME_CATEGORY_LABELS.map(({ id, label }) => ({
  id,
  label,
  themes: FLOW_THEMES.filter((theme) => theme.category === id),
}));

/** Every theme in picker order: category by category. */
export const FLOW_THEMES_BY_CATEGORY: readonly FlowThemeDefinition[] = FLOW_THEME_CATEGORIES.flatMap(
  (category) => category.themes,
);

export const isFlowThemeId = (value: unknown): value is FlowThemeId =>
  typeof value === 'string' && THEMES_BY_ID.has(value as FlowThemeId);

/** Unknown or missing values fall back to the default mountain. */
export const resolveFlowTheme = (value?: string | null): FlowThemeDefinition =>
  (isFlowThemeId(value) ? THEMES_BY_ID.get(value) : undefined) ?? (THEMES_BY_ID.get(DEFAULT_FLOW_THEME_ID) as FlowThemeDefinition);

/** Card transition for a theme, or null to keep the default horizontal slide. */
export const getFlowThemeCardMotion = (theme: FlowThemeId, isMobile: boolean): FlowThemeCardMotion | null =>
  buildFlowThemeCardMotion(THEMES_BY_ID.get(theme)?.cardMotion, isMobile);
